import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle, AlertCircle, User, Building } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useTheme } from '../context/ThemeContext';
import { TERMS_VERSION, TERMS_URL, PRIVACY_URL } from '../services/legal';

const RECAPTCHA_SITE_KEY = '6LcbB5EsAAAAAOISef2aJsbOaPKBWNY1Q_q2RwUb';

const MarsSignupForm = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessEmail: '',
    company: '',
    // The postal address is deliberately NOT asked for here. It is only needed
    // to send outreach, and demanding it — with the legal reason attached — of
    // somebody who is still deciding whether to sign up reads as an obstacle.
    // It is collected in Profile instead, prompted at the point of sending.
    password: '',
    // Both start unticked. A pre-ticked box is not acceptance, and marketing
    // is a separate decision that must never be bundled with the Terms.
    acceptTerms: false,
    marketingOptIn: false
  });
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [etherealLink, setEtherealLink] = useState('');
  const [focusedField, setFocusedField] = useState('');
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);
  const [formProgress, setFormProgress] = useState(0);
  const [submitError, setSubmitError] = useState('');

  // The Tawk.to chat widget loaded here for every visitor until 2026-08-05 —
  // a third-party cookie set before sign-up, with no consent behind it. Gone
  // with the cookie banner; this app sets strictly necessary cookies only.
  // reCAPTCHA below stays: it is a security control on the form itself.

  // Load Google reCAPTCHA v2 script
  useEffect(() => {
    if (document.querySelector('script[src*="recaptcha"]')) return;
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    window.onRecaptchaLoad = () => {
      if (recaptchaRef.current && window.grecaptcha) {
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => setRecaptchaToken(token),
          'expired-callback': () => setRecaptchaToken(''),
          theme: isDark ? 'dark' : 'light'
        });
      }
    };

    return () => { window.onRecaptchaLoad = null; };
  }, []);

  // Render reCAPTCHA when script is already loaded (e.g. after re-mount)
  useEffect(() => {
    if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current) {
      try {
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => setRecaptchaToken(token),
          'expired-callback': () => setRecaptchaToken(''),
          theme: isDark ? 'dark' : 'light'
        });
      } catch (e) {
        // Already rendered
      }
    }
  }, [isDark]);

  // Generate floating background elements
  useEffect(() => {
    const elements = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 60 + 20,
      delay: Math.random() * 2
    }));
    setFloatingElements(elements);
  }, []);

  // Helper function to validate email (no consecutive dots)
  const isEmailValid = (email) => {
    if (!email) return false;
    const trimmed = email.trim();
    if (/\.{2,}/.test(trimmed)) return false; // block consecutive dots
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  // Helper function to validate password completely
  const isPasswordValid = (password) => {
    return password && 
           password.length >= 8 && 
           /(?=.*[a-z])/.test(password) && 
           /(?=.*[A-Z])/.test(password) && 
           /(?=.*\d)/.test(password) && 
           /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password);
  };

  // Helper to check if a field value passes all validation rules
  const isFieldValueValid = (field, value) => {
    if (!value || !value.trim()) return false;
    switch (field) {
      case 'firstName':
      case 'lastName':
        return /^[a-zA-Z\s]+$/.test(value.trim()) && value.trim().length >= 2;
      case 'businessEmail':
        return isEmailValid(value);
      // Optional — a tick when it is filled in properly, nothing when it is not.
      case 'company':
        return /^[a-zA-Z0-9\s.\-&]+$/.test(value.trim()) && value.trim().length >= 2;
      case 'password':
        return isPasswordValid(value);
      default:
        return value.trim().length > 0;
    }
  };

  // Calculate form progress and validate form
  useEffect(() => {
    // Company is not counted: it is optional here, so leaving it blank must
    // still take the bar to 100% or the form looks unfinished when it isn't.
    const fields = ['firstName', 'lastName', 'businessEmail', 'password'];
    const filledFields = fields.filter(field => isFieldValueValid(field, formData[field]));

    const checkboxesValid = formData.acceptTerms && !!recaptchaToken;
    const progress = (filledFields.length / fields.length) * 80 + (checkboxesValid ? 20 : 0);

    setFormProgress(progress);
    setIsFormValid(progress === 100);
  }, [formData, recaptchaToken]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Block HTML tags and script injection on all text fields
    if (type !== 'checkbox' && /<[^>]*>/g.test(value)) {
      return;
    }

    // Only allow letters and spaces for first name and last name
    if (name === 'firstName' || name === 'lastName') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // Only allow letters, numbers, spaces, dots, hyphens, and ampersands for company
    if (name === 'company') {
      if (value && !/^[a-zA-Z0-9\s.\-&]*$/.test(value)) {
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Validate field inline as user types
    if (type !== 'checkbox' && value) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    } else if (type !== 'checkbox') {
      // Clear error if field is emptied
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError('');
    }
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'firstName':
        if (!value || !value.trim()) error = 'First name is required';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = 'Only letters are allowed';
        else if (value.trim().length < 2) error = 'First name must be at least 2 characters';
        break;
      case 'lastName':
        if (!value || !value.trim()) error = 'Last name is required';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = 'Only letters are allowed';
        else if (value.trim().length < 2) error = 'Last name must be at least 2 characters';
        break;
      case 'businessEmail':
        if (!value || !value.trim()) error = 'Business email is required';
        else if (/\.{2,}/.test(value.trim())) error = 'Email cannot contain consecutive dots';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) error = 'Invalid email format';
        break;
      // Optional: blank is fine. Anything typed still has to be usable, since
      // it ends up on invoices and in outreach.
      case 'company':
        if (!value || !value.trim()) error = '';
        else if (!/^[a-zA-Z0-9\s.\-&]+$/.test(value.trim())) error = 'Only letters, numbers, spaces, dots, hyphens and & are allowed';
        else if (value.trim().length < 2) error = 'Company name must be at least 2 characters';
        break;
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 8) error = 'Password must be at least 8 characters';
        else if (!/(?=.*[a-z])/.test(value)) error = 'Password must contain a lowercase letter';
        else if (!/(?=.*[A-Z])/.test(value)) error = 'Password must contain an uppercase letter';
        else if (!/(?=.*\d)/.test(value)) error = 'Password must contain a number';
        else if (!/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(value)) error = 'Password must contain a special character';
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    setFocusedField('');
  };

  const submitToDatabase = async (userData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://mars2.mediacle.net'}/api/createuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: userData.firstName.trim(),
          lastName: userData.lastName.trim(),
          businessEmail: userData.businessEmail.toLowerCase().trim(),
          company: userData.company.trim(),
          password: userData.password,
          recaptchaToken: recaptchaToken,
          // The acceptance record. The version travels with the request so the
          // stored evidence names the exact wording that was on screen.
          acceptTerms: userData.acceptTerms === true,
          termsVersion: TERMS_VERSION,
          marketingOptIn: userData.marketingOptIn === true,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create account');
      }

      return result;
    } catch (error) {
      console.error('Database submission error:', error);
      throw error;
    }
  };

  // Google OAuth signup — creates account, auto-verifies, redirects to pricing
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setSubmitError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const response = await fetch(`${apiUrl}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential: credentialResponse.credential })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isAuthenticated', 'true');
        window.location.href = data.redirectTo || '/pricing-plan';
      } else {
        setSubmitError(data.message || 'Google signup failed. Please try again.');
      }
    } catch (err) {
      console.error('Google signup error:', err);
      setSubmitError('Network error during Google signup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Clear previous submit errors
    setSubmitError('');
    
    // Validate all fields
    const newErrors = {};
    const fieldsToValidate = ['firstName', 'lastName', 'businessEmail', 'company', 'password'];
    
    fieldsToValidate.forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'Please confirm you have authority to bind your business and accept the Terms';
    }

    if (!recaptchaToken) {
      newErrors.recaptcha = 'Please complete the reCAPTCHA verification';
    }

    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Double check all required fields are filled
    const requiredFields = ['firstName', 'lastName', 'businessEmail', 'password'];
    const missingFields = requiredFields.filter(field => !formData[field] || !formData[field].trim());
    
    if (missingFields.length > 0) {
      setSubmitError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    if (!formData.acceptTerms || !recaptchaToken) {
      setSubmitError('Please accept the terms and complete the reCAPTCHA');
      return;
    }

    setIsLoading(true);
    
    try {
      // Submit to database
      const result = await submitToDatabase(formData);
      
      
      // Store user email and Ethereal link for verification message
      setUserEmail(formData.businessEmail);
      if (result.etherealPreviewUrl) {
        setEtherealLink(result.etherealPreviewUrl);
      }
      setSignupSuccess(true);

      // A LinkedIn Insight conversion fired here. The tag no longer loads in
      // this app (2026-08-05), so the call had nothing to talk to — measure
      // sign-up conversions on the marketing site, where consent is asked.

    } catch (error) {
      console.error('Signup failed:', error);
      setSubmitError(error.message || 'Failed to create account. Please try again.');
      // Reset reCAPTCHA on failure so user must re-verify
      setRecaptchaToken('');
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSignup = () => {
    setSignupSuccess(false);
    setUserEmail('');
    setEtherealLink('');
    setFormData({
      firstName: '',
      lastName: '',
      businessEmail: '',
      company: '',
      password: '',
      acceptTerms: false,
      marketingOptIn: false
    });
    setRecaptchaToken('');
    if (window.grecaptcha) {
      window.grecaptcha.reset();
    }
    setErrors({});
  };

  const getInputClassName = (fieldName) => {
    const baseClass = "w-full pl-10 pr-10 py-3 border-2 rounded-lg transition-all duration-300 text-sm text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm";

    if (errors[fieldName]) {
      return `${baseClass} border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:focus:ring-red-900/30`;
    } else if (focusedField === fieldName) {
      return `${baseClass} border-teal-400 dark:border-teal-500 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30`;
    } else if (formData[fieldName] && isFieldValid(fieldName)) {
      return `${baseClass} border-green-400 dark:border-green-500 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30`;
    }
    return `${baseClass} border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 focus:border-teal-400 focus:ring-2 focus:ring-teal-50 dark:focus:ring-teal-900/30`;
  };

  const getFieldIcon = (fieldName) => {
    const iconClass = `w-4 h-4 transition-all duration-300 ${
      focusedField === fieldName ? 'text-teal-600 dark:text-teal-400' :
      errors[fieldName] ? 'text-red-600 dark:text-red-400' :
      (formData[fieldName] && isFieldValid(fieldName)) ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-slate-500'
    }`;

    const icons = {
      firstName: <User className={iconClass} />,
      lastName: <User className={iconClass} />,
      businessEmail: <Mail className={iconClass} />,
      company: <Building className={iconClass} />,
      password: <Lock className={iconClass} />
    };

    return icons[fieldName];
  };

  const isFieldValid = (fieldName) => {
    return formData[fieldName] && !errors[fieldName] && isFieldValueValid(fieldName, formData[fieldName]);
  };


  // Show verification message after successful signup
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className="absolute rounded-full bg-gradient-to-r from-teal-200/10 to-blue-200/10 dark:from-teal-500/5 dark:to-blue-500/5 animate-pulse"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              width: `${element.size}px`,
              height: `${element.size}px`,
              animationDelay: `${element.delay}s`,
              animationDuration: '6s'
            }}
          />
        ))}

        {/* Verification Message Card */}
        <div className="w-full max-w-sm relative z-10">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-8 text-center">

            {/* Logo */}
            <div className="mb-6">
              <img
                src={isDark ? "/MARS-White.png" : "/MARS-Color.png"}
                alt="MARS Logo"
                className="h-10 mx-auto object-contain"
              />
            </div>

            {/* Success icon */}
            <div className="mx-auto w-14 h-14 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>

            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">
              Account Created
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
              A verification email has been sent to
            </p>

            {/* Email display */}
            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-3 mb-6">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <p className="text-sm font-medium text-gray-900 dark:text-slate-100 break-all text-left">{userEmail}</p>
              </div>
            </div>

            {/* Instructions */}
            <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed mb-6">
              Click the link in the email to verify your account. Don't see it? Check your spam folder or contact <span className="text-blue-600 dark:text-blue-400">mars@mediacle.com</span>
            </p>

            {/* Actions */}
            <a
              href="/login"
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium text-sm rounded-lg transition-colors"
            >
              Go to Login
              <ArrowRight className="w-4 h-4" />
            </a>

            <button
              onClick={handleBackToSignup}
              className="w-full mt-3 py-2 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 text-xs font-medium transition-colors"
            >
              Create Another Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // A column, not a centred row, so the legal strip sits under the card
    // rather than beside it. `m-auto` on the card keeps it centred.
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex flex-col p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      {floatingElements.map((element) => (
        <div
          key={element.id}
          className="absolute rounded-full bg-gradient-to-r from-teal-200/10 to-blue-200/10 dark:from-teal-500/5 dark:to-blue-500/5 animate-pulse"
          style={{
            left: `${element.x}%`,
            top: `${element.y}%`,
            width: `${element.size}px`,
            height: `${element.size}px`,
            animationDelay: `${element.delay}s`,
            animationDuration: '6s'
          }}
        />
      ))}

      {/* Main Container */}
      <div className="w-full max-w-md relative z-10 m-auto">

        {/* Signup Card */}
        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-xl p-6 transition-all duration-500 hover:shadow-2xl relative border border-gray-100 dark:border-slate-700">
          
          {/* Logo and Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mb-3">
              <div className="w-32 h-12">
                <img
                  src={isDark ? "/MARS-White.png" : "/MARS-Color.png"}
                  alt="MARS Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-slate-400">Join MARS and start your journey</p>
          </div>

          {/* Submit Error Display */}
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">{submitError}</p>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-slate-400 mb-2">
              <span>Complete your profile</span>
              <span className="font-medium">{Math.round(formProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
              <div 
                className={`h-1.5 rounded-full transition-all duration-700 ease-out ${
                  formProgress === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-teal-500 to-blue-500'
                }`}
                style={{ width: `${formProgress}%` }}
              />
            </div>
          </div>

          {/* Signup Form */}
          <div className="space-y-4">
            
            {/* First Name & Last Name Row */}
            <div className="grid grid-cols-2 gap-3">
              {/* First Name Field */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    {getFieldIcon('firstName')}
                  </div>
                  
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('firstName')}
                    onBlur={handleBlur}
                    placeholder="First name"
                    className={getInputClassName('firstName')}
                  />
                  
                  {isFieldValid('firstName') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                  
                  {errors.firstName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    </div>
                  )}
                </div>
                
                {errors.firstName && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name Field */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  Last Name
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    {getFieldIcon('lastName')}
                  </div>
                  
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('lastName')}
                    onBlur={handleBlur}
                    placeholder="Last name"
                    className={getInputClassName('lastName')}
                  />
                  
                  {isFieldValid('lastName') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                  
                  {errors.lastName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    </div>
                  )}
                </div>
                
                {errors.lastName && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Business Email Field */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Business Email
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  {getFieldIcon('businessEmail')}
                </div>
                
                <input
                  type="email"
                  name="businessEmail"
                  value={formData.businessEmail}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('businessEmail')}
                  onBlur={handleBlur}
                  placeholder="Enter your business email"
                  className={getInputClassName('businessEmail')}
                />
                
                {isFieldValid('businessEmail') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                )}
                
                {errors.businessEmail && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                )}
              </div>
              
              {errors.businessEmail && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.businessEmail}
                </p>
              )}
            </div>

            {/* Company Field */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Company <span className="font-normal text-gray-400 dark:text-slate-500">(optional)</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  {getFieldIcon('company')}
                </div>
                
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('company')}
                  onBlur={handleBlur}
                  placeholder="Enter your company name"
                  className={getInputClassName('company')}
                />
                
                {isFieldValid('company') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                )}
                
                {errors.company && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                )}
              </div>
              
              {errors.company && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.company}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  {getFieldIcon('password')}
                </div>
                
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={handleBlur}
                  placeholder="Create a strong password"
                  className={getInputClassName('password')}
                />
                
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  {isFieldValid('password') && !showPassword && (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  )}
                  
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition-all duration-200"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Password Requirements */}
              {formData.password && (
                <div className="mt-2 bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">Password Requirements:</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className={`flex items-center gap-1.5 ${
                      formData.password.length >= 8 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        formData.password.length >= 8 ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      8+ characters
                    </div>

                    <div className={`flex items-center gap-1.5 ${
                      /(?=.*[a-z])/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*[a-z])/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Lowercase
                    </div>

                    <div className={`flex items-center gap-1.5 ${
                      /(?=.*[A-Z])/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*[A-Z])/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Uppercase
                    </div>

                    <div className={`flex items-center gap-1.5 ${
                      /(?=.*\d)/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*\d)/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Number
                    </div>

                    <div className={`flex items-center gap-1.5 col-span-2 ${
                      /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Special character (!@#$%^&*)
                    </div>
                  </div>
                </div>
              )}
              
              {errors.password && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Terms and Robot Checker */}
            <div className="space-y-3 pt-2">
              {/* Accept Terms */}
              <div className="relative">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 border-2 rounded transition-all duration-300 flex items-center justify-center ${
                      formData.acceptTerms
                        ? 'bg-teal-500 border-teal-500'
                        : errors.acceptTerms
                        ? 'border-red-400'
                        : 'border-gray-300 dark:border-slate-600'
                    }`}>
                      {formData.acceptTerms && (
                        <CheckCircle className="w-2.5 h-2.5 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-600 dark:text-slate-400 leading-relaxed">
                    I confirm I am signing up on behalf of a business and that I have authority to
                    bind it. I have read and agree to the{' '}
                    {/* Real pages, opened in a new tab: a modal cannot be
                        linked to, cited in a dispute, or read after sign-up.
                        Both are published on mars.mediacle.com — the address
                        the documents themselves cite as the current version.

                        These links sit inside the <label>, and a label forwards
                        every click within it to its checkbox. That forwarding
                        ran first and swallowed the link, so the first click only
                        ticked the box and the document opened on the second.
                        Stopping the click at the link leaves it a plain link:
                        one click opens it, and consent is not given by someone
                        who was only trying to read what they are agreeing to.
                        Clicking the wording still ticks the box as before. */}
                    <a
                      href={TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium hover:underline"
                    >
                      Terms of Service
                    </a>{' '}
                    (including the Data Processing Agreement) and the{' '}
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium hover:underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
                {errors.acceptTerms && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.acceptTerms}
                  </p>
                )}
              </div>

              {/* Informational, not a condition of anything. */}
              <p className="text-[11px] leading-relaxed text-gray-500 dark:text-slate-400">
                MARS™ is provided to businesses only and is not available to consumers. Paid plans
                renew monthly until cancelled. All prices exclude VAT.
              </p>

              {/* Marketing consent — separate, unticked, and never a condition
                  of signing up. Bundling it with the Terms would make both
                  unsound. */}
              <div className="relative">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      name="marketingOptIn"
                      checked={formData.marketingOptIn}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 border-2 rounded transition-all duration-300 flex items-center justify-center ${
                      formData.marketingOptIn
                        ? 'bg-teal-500 border-teal-500'
                        : 'border-gray-300 dark:border-slate-600'
                    }`}>
                      {formData.marketingOptIn && (
                        <CheckCircle className="w-2.5 h-2.5 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-600 dark:text-slate-400 leading-relaxed">
                    Send me occasional product updates and iGaming affiliate marketing insights from
                    Mediacle. You can unsubscribe at any time.
                  </span>
                </label>
              </div>

              {/* Google reCAPTCHA v2 */}
              <div className="relative">
                <div ref={recaptchaRef} className="flex justify-center"></div>
                {errors.recaptcha && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.recaptcha}
                  </p>
                )}
              </div>
            </div>

            {/* Create Account Button */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid || isLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium text-sm text-white transition-all duration-500 relative overflow-hidden ${
                !isFormValid || isLoading
                  ? 'bg-gray-400 dark:bg-slate-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-md hover:shadow-lg'
              }`}
            >
              <div className="relative flex items-center justify-center gap-2 h-5">
                {/* Loading state */}
                <div className={`absolute inset-0 flex items-center justify-center gap-2 transition-all duration-500 ${
                  isLoading ? 'opacity-100' : 'opacity-0'
                }`}>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Creating Account...</span>
                </div>

                {/* Default state */}
                <div className={`absolute inset-0 flex items-center justify-center gap-2 transition-all duration-500 ${
                  !isLoading ? 'opacity-100' : 'opacity-0'
                }`}>
                  <span>Create Account</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </button>

            {/* Google Sign-Up — uncomment to enable
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white dark:bg-slate-900 text-gray-400 dark:text-slate-500">or sign up with</span>
              </div>
            </div>
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setSubmitError('Google signup failed. Please try again.')}
                theme={isDark ? 'filled_black' : 'outline'}
                size="large"
                width="100%"
                text="signup_with"
                shape="rectangular"
              />
            </div>
            */}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center border-t border-gray-100 dark:border-slate-700 pt-4">
            <p className="text-xs text-gray-600 dark:text-slate-400">
              Already have an account?{' '}
              <a
                href="/login"
                className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium transition-all duration-300 hover:underline transform hover:scale-105"
              >
                Sign in here
              </a>
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default MarsSignupForm;